export const NonretriableSyncFailure = 'Nonretriable Sync failure'

/**
 * Old starting key for docs not yet synced to central DB
 */
export const minLocalSeqOld = 1000000000
export const maxLocalSeqOld = 2000000000

/**
 * (New) Starting key for docs not yet synced to central DB
 * '24' represents '24000000000000000000000000000000000000'
 * NOTE: '24' will sort after '2100' (MAX_LAN_SEQ)
 */
export const MIN_LOCAL_SEQ = '24'
export const MAX_LOCAL_SEQ = '99'

/**
 * Starting key for lan docs not yet synced to central DB
 * '2024' represents '2024-00-00_00-00-00-000
 * NOTE: this will sort before '24' (MIN_LOCAL_SEQ)
 */
export const MIN_LAN_SEQ = '2024'
export const MAX_LAN_SEQ = '2100'

export interface IRemoteDBItem {
    project: string,
    seq: number,
    doc: IDBModDoc,
}

export interface IDBItem extends IDBItemCore {
    project: string,
    seq: number | string,
    doc: IDBModDoc,
}

export interface IDBItemCore {
    seq: number | string
    doc: IDBModDoc
}

// This is what we store in level-js-PROJECT-db
export type DBEntry = {
    key: number | string // local
    doc: IDBModDoc // IDBModDoc to be passed to DBAcceptor
}

export type NextDbItem = { key: string | number | undefined, value: IDBModDoc | undefined }

export interface IDBIterator {
    next(): Promise<NextDbItem>
    end(): Promise<{ key: null, value: null }>
}

export type IDBOperations = {
    /**
     * @param seqStart (exclusive)
     * @param seqEnd (inclusive)
     * @returns IDBObject[seq] where seqStart < seq <= seqEnd
     */
    get(seqStart: number | string, seqEnd: number | string): Promise<IDBModDoc[]> // seqStart < seq <= seqEnd
    put(seq: number | string, doc: IDBObject): Promise<void>
    deleteDoc(key: number | string): Promise<void>
    writeItems(items: IDBItem[]): Promise<void>
    readKeys(): Promise<(number | string)[]>
    readDBRecords(): Promise<DBEntry[]>
    /**
     * should be used in a try-finally block where finally calls iterator.end()
     * @param currentKey (inclusive)
     */
    getIterator<T extends string | number>(currentKey: T): IDBIterator
    count(): Promise<number>
    deleteDB(): Promise<void>
    close(): void
}

export type IDBSyncApi = {
    loggedIn: () => boolean,
    sync: (name: string, docs: IDBModDoc[], remoteSeq: number, dbId?: string) => Promise<IRemoteDBItem[]>,
    blockServerUpdates: () => boolean,
    delayUntilOnline: (logContext: string) => Promise<void>,
    syncCount?: number, // number of sync operations
}

export interface IDBObject {
    _id: string
    removed?: boolean
    viewed?: boolean
}

export interface IDBModDoc extends IDBObject {
    modDate: string
    _idModDateProtectionBypass?: boolean
    modBy?: string
}

export interface IDBAcceptor {
    accept: (doc: IDBObject, label?: string, seq?: number | string) => void
    fixProjectPlanIds: () => void
}

export interface IDB {
    initialize: (acceptor: IDBAcceptor, progress: (message: string) => void) => Promise<number>
    put: (obj: IDBModDoc) => Promise<void>
    get(seqStart?: number | string, seqEnd?: number | string): Promise<IDBModDoc[]>
    delete: (_id: string, _lastId?: string) => Promise<void>
    getNewId: (existing: IDBObject[], date: Date, tag?: string) => string
    username: string
    getDate: () => string
    doSync: () => void
    deleteDB: () => Promise<void>
    getRemoteSeq: () => number
    submitChange: (obj: IDBModDoc) => void,
    disconnect: () => void,
    getMaxToSyncSize: () => number,

    // Unit testing only! should be separate interface
    slice: () => any[]
    reset: (nextId: number) => void
}

